import React from 'react';
import LayoutIconCenteredLogo from '@honeybook/icons/dist/icons/LayoutIconCenteredLogo';
import LayoutIconRightLogo from '@honeybook/icons/dist/icons/LayoutIconRightLogo';
import LayoutIconLeftFooter from '@honeybook/icons/dist/icons/LayoutIconLeftFooter';
import LayoutIconStackedLeft from '@honeybook/icons/dist/icons/LayoutIconStackedLeft';
import LayoutIconCenteredFooter from '@honeybook/icons/dist/icons/LayoutIconCenteredFooter';
import LayoutIconLeftLogo from '@honeybook/icons/dist/icons/LayoutIconLeftLogo';
import LayoutIconHeader from '@honeybook/icons/dist/icons/CompanyIconHeader';
import LayoutIconLogoOnly from '@honeybook/icons/dist/icons/LayoutIconLogoOnly';
import LayoutIconTitleOnly from '@honeybook/icons/dist/icons/LayoutIconTitleOnly';
import { CompanyInfoLayoutType } from '~/types/builder.types';
import {
    Alignment,
    ICompanyInfoStyles,
    IconColor,
    IconSize
} from '~/types/block-styles.types';
import { getFullUrl } from '~/utils/url.utils';
import { Company } from '~/models/company.model';
import { ICompositionLayoutTypeConfig } from '~/components/Builder/BuilderSidebar/ContainerSidebar/composition-layout.types';

export const DEFAULT_COMPANY_INFO_LAYOUT = CompanyInfoLayoutType.LogoOnLeft;

export function getCompanyInfoLayouts(company: Partial<Company>): {
    defaultLayout: ICompositionLayoutTypeConfig;
    layouts: Record<CompanyInfoLayoutType, ICompositionLayoutTypeConfig>;
} {
    const {
        company_name,
        email,
        phone_number,
        street_address,
        zip_code,
        city,
        state,
        icon_image,
        logo_image,
        website_url,
        owner: { full_name }
    } = company;

    const companyInfo = (style: ICompanyInfoStyles) => {
        const { alignment, title_font_size, details_font_size } = style;
        const ownerInfo = [full_name, phone_number, email]
            .filter(Boolean)
            .join(' | ');
        const location = [street_address, city, state, zip_code]
            .filter(Boolean)
            .join(', ');

        return `
        <p style="text-align:${alignment};">
            <span id="company-name" style="font-size:${title_font_size}px;line-height:32px;"><strong>
            ${company_name || ''}</strong></span><br>
            <span id="owner-info" style="font-size:${details_font_size}px;line-height:26px;">
            ${ownerInfo} 
            </span><br>
            <span id="location" style="font-size:${details_font_size}px;line-height:26px;">
            ${location}
            </span><br>
            <a style="font-size:16px;line-height:22px;" href="${getFullUrl(
                website_url || ''
            )}">
            ${website_url || ''}</a>
        </p>`;
    };

    const companyInfoHero = (style: ICompanyInfoStyles) => {
        const { alignment, title_font_size, details_font_size } = style;
        return `
        <p style="text-align:${alignment}">
            <span style="font-size:${title_font_size}px;line-height:32px;color:white;"><strong>
            ${company_name || ''}</strong></span><br>
            <span style="font-size:${details_font_size}px;line-height:26px;color:white;">
            ${full_name || ''}</span><br>
        </p>`;
    };

    const companyInfoHeader = (style: ICompanyInfoStyles) => {
        const { alignment, title_font_size } = style;
        return `
        <p style="text-align:${alignment};">
            <span style="font-size:${title_font_size}px;line-height:32px;"><strong>
            ${company_name || ''}</strong></span><br>
        </p>`;
    };

    const { position_w, position_x, position_y } = icon_image || {};
    const imageData = {
        x: position_x,
        y: position_y,
        crop_ratio: '1:1',
        resize_ratio: 100 / position_w
    } as const;

    const layouts: Record<CompanyInfoLayoutType, ICompositionLayoutTypeConfig> =
        {
            [CompanyInfoLayoutType.LogoOnLeft]: {
                title: 'Logo on left',
                icon: <LayoutIconLeftLogo useDefaultFill />,
                smallIcon: <LayoutIconLeftLogo useDefaultFill />,
                type: CompanyInfoLayoutType.LogoOnLeft,
                layoutStruct: {
                    type: 'company_info_block',
                    company_info_layout_type: CompanyInfoLayoutType.LogoOnLeft,
                    layout_type: 'horizontal',
                    container_styles: { grid_gap: 40 },
                    children: [
                        {
                            type: 'container_block',
                            layout_type: 'vertical',
                            size: 14.5,
                            children: [
                                {
                                    type: 'image_block',
                                    image: { ...icon_image, ...imageData },
                                    image_styles: {
                                        alignment: Alignment.LEFT
                                    }
                                }
                            ]
                        },
                        {
                            type: 'container_block',
                            layout_type: 'vertical',
                            size: 85.5,
                            children: [
                                {
                                    type: 'text_block',
                                    text_content: companyInfo({
                                        alignment: 'left',
                                        title_font_size: 24,
                                        details_font_size: 18
                                    })
                                },
                                {
                                    type: 'spacer_block',
                                    spacer_styles: { height: 10 }
                                },
                                {
                                    type: 'social_block',
                                    social_styles: {
                                        icon_alignment: Alignment.LEFT
                                    }
                                }
                            ]
                        }
                    ]
                }
            },
            [CompanyInfoLayoutType.Centered]: {
                title: 'Centered',
                icon: <LayoutIconCenteredLogo useDefaultFill />,
                smallIcon: <LayoutIconCenteredLogo useDefaultFill />,
                type: CompanyInfoLayoutType.Centered,
                layoutStruct: {
                    type: 'company_info_block',
                    company_info_layout_type: CompanyInfoLayoutType.Centered,
                    layout_type: 'horizontal',
                    children: [
                        {
                            type: 'container_block',
                            layout_type: 'vertical',
                            size: 25,
                            children: [
                                {
                                    type: 'image_block',
                                    image: { ...icon_image, ...imageData }
                                },
                                {
                                    type: 'spacer_block',
                                    spacer_styles: { height: 24 }
                                },
                                {
                                    type: 'text_block',
                                    text_content: companyInfo({
                                        alignment: 'center',
                                        title_font_size: 24,
                                        details_font_size: 18
                                    })
                                },
                                {
                                    type: 'spacer_block',
                                    spacer_styles: { height: 10 }
                                },
                                {
                                    type: 'social_block',
                                    social_styles: {
                                        icon_alignment: Alignment.CENTER
                                    }
                                }
                            ]
                        }
                    ]
                }
            },
            [CompanyInfoLayoutType.LogoOnRight]: {
                title: 'Logo on right',
                icon: <LayoutIconRightLogo useDefaultFill />,
                smallIcon: <LayoutIconRightLogo useDefaultFill />,
                type: CompanyInfoLayoutType.LogoOnRight,
                layoutStruct: {
                    type: 'company_info_block',
                    company_info_layout_type: CompanyInfoLayoutType.LogoOnRight,
                    layout_type: 'horizontal',
                    container_styles: { grid_gap: 40 },
                    children: [
                        {
                            type: 'container_block',
                            layout_type: 'vertical',
                            size: 85.5,
                            children: [
                                {
                                    type: 'text_block',
                                    text_content: companyInfo({
                                        alignment: 'right',
                                        title_font_size: 24,
                                        details_font_size: 18
                                    })
                                },
                                {
                                    type: 'spacer_block',
                                    spacer_styles: { height: 10 }
                                },
                                {
                                    type: 'social_block',
                                    social_styles: {
                                        icon_alignment: Alignment.RIGHT
                                    }
                                }
                            ]
                        },
                        {
                            type: 'container_block',
                            layout_type: 'vertical',
                            size: 14.5,
                            children: [
                                {
                                    type: 'image_block',
                                    image: { ...icon_image, ...imageData },
                                    image_styles: {
                                        alignment: Alignment.RIGHT
                                    }
                                }
                            ]
                        }
                    ]
                }
            },
            [CompanyInfoLayoutType.Footer]: {
                title: 'Footer',
                icon: <LayoutIconLeftFooter useDefaultFill />,
                smallIcon: <LayoutIconLeftFooter useDefaultFill />,
                type: CompanyInfoLayoutType.Footer,
                layoutStruct: {
                    type: 'company_info_block',
                    company_info_layout_type: CompanyInfoLayoutType.Footer,
                    layout_type: 'horizontal',
                    container_styles: { grid_gap: 40 },
                    children: [
                        {
                            type: 'container_block',
                            layout_type: 'vertical',
                            size: 25,
                            children: [
                                {
                                    type: 'text_block',
                                    text_content: companyInfo({
                                        alignment: 'left',
                                        title_font_size: 18,
                                        details_font_size: 16
                                    })
                                },
                                {
                                    type: 'spacer_block',
                                    spacer_styles: { height: 10 }
                                },
                                {
                                    type: 'social_block',
                                    social_styles: {
                                        icon_alignment: Alignment.LEFT,
                                        icon_size: IconSize.SMALL
                                    }
                                },
                                {
                                    type: 'spacer_block',
                                    spacer_styles: { height: 10 }
                                },
                                {
                                    type: 'image_block',
                                    image: {
                                        ...logo_image,
                                        width: 120,
                                        height: 40
                                    },
                                    image_styles: {
                                        alignment: Alignment.LEFT
                                    }
                                }
                            ]
                        }
                    ]
                }
            },
            [CompanyInfoLayoutType.FooterCentered]: {
                title: 'Footer centered',
                icon: <LayoutIconCenteredFooter useDefaultFill />,
                smallIcon: <LayoutIconCenteredFooter useDefaultFill />,
                type: CompanyInfoLayoutType.FooterCentered,
                layoutStruct: {
                    type: 'company_info_block',
                    company_info_layout_type:
                        CompanyInfoLayoutType.FooterCentered,
                    layout_type: 'horizontal',
                    children: [
                        {
                            type: 'container_block',
                            layout_type: 'vertical',
                            size: 25,
                            children: [
                                {
                                    type: 'text_block',
                                    text_content: companyInfo({
                                        alignment: 'center',
                                        title_font_size: 18,
                                        details_font_size: 16
                                    })
                                },
                                {
                                    type: 'spacer_block',
                                    spacer_styles: { height: 10 }
                                },
                                {
                                    type: 'social_block',
                                    social_styles: {
                                        icon_alignment: Alignment.CENTER,
                                        icon_size: IconSize.SMALL
                                    }
                                },
                                {
                                    type: 'spacer_block',
                                    spacer_styles: { height: 10 }
                                },
                                {
                                    type: 'image_block',
                                    image: {
                                        ...logo_image,
                                        width: 120,
                                        height: 40
                                    }
                                }
                            ]
                        }
                    ]
                }
            },
            [CompanyInfoLayoutType.StackedLeft]: {
                title: 'Stacked left',
                icon: <LayoutIconStackedLeft useDefaultFill />,
                smallIcon: <LayoutIconStackedLeft useDefaultFill />,
                type: CompanyInfoLayoutType.StackedLeft,
                layoutStruct: {
                    type: 'company_info_block',
                    company_info_layout_type: CompanyInfoLayoutType.StackedLeft,
                    layout_type: 'horizontal',
                    container_styles: { grid_gap: 40 },
                    children: [
                        {
                            type: 'container_block',
                            layout_type: 'vertical',
                            size: 68.5,
                            children: [
                                {
                                    type: 'image_block',
                                    image: {
                                        ...logo_image,
                                        width: 180,
                                        height: 60
                                    }
                                },
                                {
                                    type: 'spacer_block',
                                    spacer_styles: { height: 10 }
                                },
                                {
                                    type: 'text_block',
                                    text_content: companyInfo({
                                        alignment: 'center',
                                        title_font_size: 24,
                                        details_font_size: 18
                                    })
                                },
                                {
                                    type: 'spacer_block',
                                    spacer_styles: { height: 10 }
                                },
                                {
                                    type: 'social_block',
                                    social_styles: {
                                        icon_alignment: Alignment.CENTER
                                    }
                                }
                            ]
                        },
                        {
                            type: 'container_block',
                            layout_type: 'vertical',
                            size: 31.5,
                            children: [
                                {
                                    type: 'image_block'
                                }
                            ]
                        }
                    ]
                }
            },
            [CompanyInfoLayoutType.Hero]: {
                title: 'Hero',
                icon: <LayoutIconHeader useDefaultFill />,
                smallIcon: <LayoutIconHeader useDefaultFill />,
                type: CompanyInfoLayoutType.Hero,
                layoutStruct: {
                    type: 'company_info_block',
                    company_info_layout_type: CompanyInfoLayoutType.Hero,
                    layout_type: 'horizontal',
                    background_image: { url: null },
                    container_styles: {
                        grid_gap: 20,
                        background_color: '#000000',
                        background_color_opacity: 20
                    },
                    children: [
                        {
                            type: 'container_block',
                            layout_type: 'vertical',
                            size: 14.5,
                            children: [
                                {
                                    type: 'spacer_block',
                                    spacer_styles: {
                                        height: 100,
                                        is_hidden: true
                                    }
                                },
                                {
                                    type: 'image_block',
                                    image: { ...icon_image, ...imageData },
                                    image_styles: {
                                        alignment: Alignment.LEFT
                                    }
                                }
                            ]
                        },
                        {
                            type: 'container_block',
                            layout_type: 'vertical',
                            size: 85.5,
                            children: [
                                {
                                    type: 'spacer_block',
                                    spacer_styles: {
                                        height: 100,
                                        is_hidden: true
                                    }
                                },
                                {
                                    type: 'text_block',
                                    text_content: companyInfoHero({
                                        alignment: 'left',
                                        title_font_size: 24,
                                        details_font_size: 18
                                    })
                                },
                                {
                                    type: 'social_block',
                                    social_styles: {
                                        icon_alignment: Alignment.LEFT,
                                        icon_color: IconColor.WHITE
                                    }
                                }
                            ]
                        }
                    ]
                }
            },
            [CompanyInfoLayoutType.Header]: {
                title: 'Header',
                icon: <LayoutIconTitleOnly useDefaultFill />,
                smallIcon: <LayoutIconTitleOnly useDefaultFill />,
                type: CompanyInfoLayoutType.Header,
                layoutStruct: {
                    type: 'company_info_block',
                    company_info_layout_type: CompanyInfoLayoutType.Header,
                    layout_type: 'horizontal',
                    children: [
                        {
                            type: 'container_block',
                            layout_type: 'vertical',
                            size: 25,
                            children: [
                                {
                                    type: 'image_block',
                                    image: { ...icon_image, ...imageData }
                                },
                                {
                                    type: 'spacer_block',
                                    spacer_styles: { height: 24 }
                                },
                                {
                                    type: 'text_block',
                                    text_content: companyInfoHeader({
                                        alignment: 'center',
                                        title_font_size: 24
                                    })
                                }
                            ]
                        }
                    ]
                }
            },
            [CompanyInfoLayoutType.LogoOnly]: {
                title: 'Logo',
                icon: <LayoutIconLogoOnly useDefaultFill />,
                smallIcon: <LayoutIconLogoOnly useDefaultFill />,
                type: CompanyInfoLayoutType.LogoOnly,
                layoutStruct: {
                    type: 'company_info_block',
                    layout_type: 'horizontal',
                    container_styles: {},
                    children: [
                        {
                            type: 'container_block',
                            layout_type: 'vertical',
                            children: [
                                {
                                    type: 'image_block',
                                    image: Object.assign(
                                        {},
                                        company.icon_image,
                                        imageData,
                                        {
                                            crop_ratio: 'Circle'
                                        }
                                    ),
                                    image_styles: {
                                        alignment: Alignment.CENTER,
                                        border_radius: 100
                                    }
                                }
                            ]
                        }
                    ]
                }
            }
        };

    return {
        layouts,
        defaultLayout: layouts[DEFAULT_COMPANY_INFO_LAYOUT]
    };
}
